"use client";

import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { useWebSocketConnection } from "@/hooks/useWebSocket";
import { useSessionContext } from "@/context/SessionContext";
import { FeatureFlagName } from "@/types/feature-flag";
import {
  CloudEvent,
  getChannelFromCloudEventType,
  getEntityIdFromCloudEvent,
} from "@/types/cloud-events";

/**
 * CloudEvents message structure received from WebSocket
 */
type WebSocketMessage = CloudEvent;

type MessageHandler = (message: CloudEvent) => void;

interface Subscription {
  channel: string;
  entityId?: string;
  handlers: Set<MessageHandler>;
  pendingUnsubscribe?: ReturnType<typeof setTimeout>;
}

export interface WebSocketContextValue {
  isConnected: boolean;
  connectionState:
    | "connecting"
    | "open"
    | "closing"
    | "closed"
    | "uninstantiated";
  /**
   * True only when the socket is fully connected and healthy
   * (`connectionState === "open"`). This is the single signal that polling
   * fallbacks gate on — poll only when the socket is NOT healthy.
   */
  isHealthy: boolean;
  /**
   * Increments only on a genuine reconnect (a `disconnected → connected`
   * transition), never on the first connect. Consumers that must reconcile
   * state after an outage key off this value (see `useReconnectRefetch`).
   */
  reconnectNonce: number;
  /**
   * Subscribe to CloudEvents for a channel with an optional entityId.
   * Returns an unsubscribe function.
   *
   * @example
   * // Subscribe to a specific conversation's events
   * const unsubscribe = subscribe('conversation', conversationId, (event) => {
   *   // event is a CloudEvent with type like 'com.focal.conversation.status-updated'
   *   // Data is minimal (IDs only) - fetch full data via REST API
   *   const { conversationId, status } = event.data;
   * });
   *
   * // Later, unsubscribe
   * unsubscribe();
   */
  subscribe: (
    channel: string,
    entityId: string | undefined,
    handler: MessageHandler,
  ) => () => void;
  /**
   * Send a message through the WebSocket
   */
  sendMessage: (message: any) => void;
}

// Exported so component tests can provide a fake connection state.
export const WebSocketContext = createContext<WebSocketContextValue | null>(
  null,
);

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
}) => {
  const { user } = useSessionContext();
  const subscriptionsRef = useRef<Map<string, Subscription>>(new Map());

  // Refs for stable subscribe function - prevents re-renders from changing subscribe identity
  const isConnectedRef = useRef(false);
  const socketSubscribeRef = useRef<
    (channel: string, entityId?: string) => void
  >(() => {});
  const socketUnsubscribeRef = useRef<
    (channel: string, entityId?: string) => void
  >(() => {});

  // Batch subscribe messages to prevent flooding the server
  const pendingSubscribesRef = useRef<Set<string>>(new Set());
  const subscribeBatchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Generate subscription key
  const getSubscriptionKey = useCallback(
    (channel: string, entityId?: string) => {
      return entityId ? `${channel}:${entityId}` : channel;
    },
    [],
  );

  // Flush pending subscribes - sends batched subscribe messages to backend
  const flushPendingSubscribes = useCallback(() => {
    if (subscribeBatchTimeoutRef.current) {
      clearTimeout(subscribeBatchTimeoutRef.current);
      subscribeBatchTimeoutRef.current = null;
    }

    if (pendingSubscribesRef.current.size === 0) return;
    if (!isConnectedRef.current) return;

    // Send all pending subscribes
    pendingSubscribesRef.current.forEach((key) => {
      const subscription = subscriptionsRef.current.get(key);
      if (subscription && subscription.handlers.size > 0) {
        socketSubscribeRef.current(subscription.channel, subscription.entityId);
      }
    });

    pendingSubscribesRef.current.clear();
  }, []);

  // Queue a subscribe to be sent (batched)
  const queueSubscribe = useCallback(
    (key: string) => {
      pendingSubscribesRef.current.add(key);

      // Clear existing timeout and set a new one
      if (subscribeBatchTimeoutRef.current) {
        clearTimeout(subscribeBatchTimeoutRef.current);
      }

      // Batch subscribes - wait 50ms for more subscribes before sending
      subscribeBatchTimeoutRef.current = setTimeout(() => {
        flushPendingSubscribes();
      }, 50);
    },
    [flushPendingSubscribes],
  );

  // Handle incoming CloudEvents messages and route to appropriate handlers
  const handleMessage = useCallback((message: WebSocketMessage) => {
    // Extract channel and entity ID from CloudEvent
    const messageChannel = getChannelFromCloudEventType(message.type);
    const messageEntityId = getEntityIdFromCloudEvent(message);

    if (!messageChannel) {
      console.warn("Unknown CloudEvent type:", message.type);
      return;
    }

    // Route message to all matching subscription handlers
    subscriptionsRef.current.forEach((subscription) => {
      const channelMatches = subscription.channel === messageChannel;
      const entityMatches =
        !subscription.entityId || subscription.entityId === messageEntityId;

      if (channelMatches && entityMatches) {
        subscription.handlers.forEach((handler) => {
          try {
            handler(message);
          } catch (error) {
            console.error("WebSocket handler error:", error);
          }
        });
      }
    });
  }, []);

  // Initialize the single WebSocket connection
  const {
    isConnected,
    connectionState,
    reconnectNonce,
    sendMessage,
    subscribe: socketSubscribe,
    unsubscribe: socketUnsubscribe,
  } = useWebSocketConnection({
    enabled: !!user?.id,
    onMessage: handleMessage,
    onOpen: () => {
      // Resubscribe to all active subscriptions when reconnected (batched)
      subscriptionsRef.current.forEach((subscription, key) => {
        if (subscription.handlers.size > 0) {
          pendingSubscribesRef.current.add(key);
        }
      });
      // Flush after a short delay to batch with any new subscriptions
      if (subscribeBatchTimeoutRef.current) {
        clearTimeout(subscribeBatchTimeoutRef.current);
      }
      subscribeBatchTimeoutRef.current = setTimeout(() => {
        flushPendingSubscribes();
      }, 100);
    },
  });

  // Keep refs in sync with current values for stable subscribe function
  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  useEffect(() => {
    socketSubscribeRef.current = socketSubscribe;
    socketUnsubscribeRef.current = socketUnsubscribe;
  }, [socketSubscribe, socketUnsubscribe]);

  // Subscribe to a channel - returns unsubscribe function
  // Uses refs to keep this callback stable and prevent unnecessary re-subscriptions
  const subscribe = useCallback(
    (
      channel: string,
      entityId: string | undefined,
      handler: MessageHandler,
    ): (() => void) => {
      const key = getSubscriptionKey(channel, entityId);

      let subscription = subscriptionsRef.current.get(key);

      if (!subscription) {
        // Create new subscription
        subscription = {
          channel,
          entityId,
          handlers: new Set(),
        };
        subscriptionsRef.current.set(key, subscription);

        // Queue subscribe message to be sent (batched) if connected
        if (isConnectedRef.current) {
          queueSubscribe(key);
        }
      } else if (subscription.pendingUnsubscribe) {
        // Cancel pending unsubscribe if another handler is being added
        clearTimeout(subscription.pendingUnsubscribe);
        subscription.pendingUnsubscribe = undefined;
      }

      // Add handler
      subscription.handlers.add(handler);

      // Return unsubscribe function
      return () => {
        const sub = subscriptionsRef.current.get(key);
        if (sub) {
          sub.handlers.delete(handler);

          // If no more handlers, schedule unsubscribe with a short delay
          // This prevents rapid subscribe/unsubscribe cycles during React's
          // effect cleanup/setup phase when multiple components mount/unmount
          if (sub.handlers.size === 0 && !sub.pendingUnsubscribe) {
            sub.pendingUnsubscribe = setTimeout(() => {
              // Re-check if still no handlers after delay
              const currentSub = subscriptionsRef.current.get(key);
              if (currentSub && currentSub.handlers.size === 0) {
                if (isConnectedRef.current) {
                  socketUnsubscribeRef.current(channel, entityId);
                }
                subscriptionsRef.current.delete(key);
              }
            }, 100); // 100ms delay to allow other components to subscribe
          }
        }
      };
    },
    [getSubscriptionKey, queueSubscribe],
  );

  // Cleanup pending unsubscribe timeouts and batch timeout on unmount
  useEffect(() => {
    return () => {
      subscriptionsRef.current.forEach((subscription) => {
        if (subscription.pendingUnsubscribe) {
          clearTimeout(subscription.pendingUnsubscribe);
        }
      });
      if (subscribeBatchTimeoutRef.current) {
        clearTimeout(subscribeBatchTimeoutRef.current);
      }
    };
  }, []);

  // The socket is "healthy" only when fully open. Derived from connectionState
  // so it stays in lockstep with isConnected without a separate source of truth.
  const isHealthy = connectionState === "open" && isConnected;

  const contextValue: WebSocketContextValue = {
    isConnected,
    connectionState,
    isHealthy,
    reconnectNonce,
    subscribe,
    sendMessage,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Hook to access the global WebSocket connection.
 * Must be used within WebSocketProvider.
 */
export const useWebSocket = (): WebSocketContextValue => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within WebSocketProvider");
  }
  return context;
};

/**
 * Hook to subscribe to WebSocket CloudEvents for a specific channel/entity.
 * Automatically handles subscribe/unsubscribe lifecycle.
 *
 * @example
 * // In a conversation details component:
 * useWebSocketSubscription('conversation', conversationId, (event) => {
 *   if (event.type === CloudEventType.CONVERSATION_STATUS_UPDATED) {
 *     const { conversationId, status } = event.data;
 *     // Handle status update - fetch full data via REST if needed
 *   }
 * });
 */
export const useWebSocketSubscription = (
  channel: string,
  entityId: string | undefined,
  handler: MessageHandler,
  enabled: boolean = true,
): { isConnected: boolean } => {
  const { subscribe, isConnected } = useWebSocket();
  const handlerRef = useRef(handler);

  // Keep handler ref updated
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  // Stable handler that uses ref
  const stableHandler = useCallback((message: WebSocketMessage) => {
    handlerRef.current(message);
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const unsubscribe = subscribe(channel, entityId, stableHandler);

    return () => {
      unsubscribe();
    };
  }, [channel, entityId, enabled, subscribe, stableHandler]);

  return { isConnected };
};
